import {
    type CSSProperties,
    type ReactNode,
    useEffect,
    Fragment,
    useState
} from "react";
import {
    SAMPLE_PHOTO_SOURCE,
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type AspectRatioPreset,
    ToggleGroup,
    AspectRatio,
    Button,
    Text
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "AspectRatio";

const USAGE_CODE = `<AspectRatio
    alt="Sunset behind the mountains"
    source={{
        uri: photo.url
    }}
    ratio="16:9"
/>

<AspectRatio
    ratio={2.4}
    alt="Weekly summary"
>
    <Text>
        Any content sits in the same frame.
    </Text>
</AspectRatio>`;

const STATES_CODE = `<AspectRatio
    source={{
        uri: photo.url
    }}
    onRetry={handleRetry}
    alt="Quarterly report"
    ratio="4:3"
/>

<AspectRatio
    isRestricted={true}
    alt="Quarterly report"
    ratio="4:3"
/>`;

const RATIOS: Array<AspectRatioPreset> = [
    "1:1",
    "4:3",
    "16:9",
    "21:9",
    "3:4"
];

const RADIUS_ROLES = [
    "surface",
    "form",
    "flush"
] as const;

const BROKEN_SOURCE = {
    uri: "data:image/png;base64,AAAA"
};

const RELOAD_DELAY = 1500;

const SMALL_CELL_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex",
    width: 160,
    gap: 8
};

const MEDIUM_CELL_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex",
    width: 240,
    gap: 8
};

const CHILDREN_TEXT_STYLE = {
    padding: 16
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("aspectRatioPage.fitTitle"),
        id: "fit"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("aspectRatioPage.cornersTitle"),
        id: "corners"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const AspectRatioPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        ratio,
        setRatio
    ] = useState<AspectRatioPreset>("16:9");

    const [
        isReloading,
        setIsReloading
    ] = useState(false);

    useEffect(() => {
        if(!isReloading) {
            return;
        }

        const timer = window.setTimeout(() => setIsReloading(false), RELOAD_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isReloading
    ]);

    const handleRatioChange = (value: string | Array<string>) => {
        const nextRatio = RATIOS.find((item) => item === value);

        if(nextRatio) {
            setRatio(nextRatio);
        }
    };

    const handleReload = () => setIsReloading(true);

    const renderCell = (label: string, node: ReactNode, style: CSSProperties = SMALL_CELL_STYLE) => <div
        style={style}
        key={label}
    >
        <Text
            variant="labelSmallSize"
            color="mid"
        >
            {label}
        </Text>
        {node}
    </div>;

    return <Article>
        <ComponentHeader
            lead={t("aspectRatioPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("aspectRatioPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("aspectRatioPage.overviewCaption")}
                isStacked={true}
            >
                <ToggleGroup
                    title={t("aspectRatioPage.ratioLabel")}
                    onValueChange={handleRatioChange}
                    attachBehaviour="attached"
                    selectBehaviour="single"
                    isClearEnabled={false}
                    variant="outline"
                    value={ratio}
                    size="small"
                >
                    {
                        RATIOS.map((item) => <ToggleGroup.Item
                            title={item}
                            value={item}
                            key={item}
                        />)
                    }
                </ToggleGroup>
                <AspectRatio
                    alt={t("aspectRatioPage.imageAlt")}
                    source={SAMPLE_PHOTO_SOURCE}
                    ratio={ratio}
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("aspectRatioPage.fitTitle")}
            id="fit"
        >
            <Paragraph>
                {t("aspectRatioPage.fitText")}
            </Paragraph>
            <Preview>
                {
                    renderCell("objectFit=\"cover\"", <AspectRatio
                        alt={t("aspectRatioPage.imageAlt")}
                        source={SAMPLE_PHOTO_SOURCE}
                        objectFit="cover"
                        ratio="1:1"
                    />)
                }
                {
                    renderCell("objectFit=\"contain\"", <AspectRatio
                        alt={t("aspectRatioPage.imageAlt")}
                        source={SAMPLE_PHOTO_SOURCE}
                        objectFit="contain"
                        ratio="1:1"
                    />)
                }
                {
                    renderCell("children", <AspectRatio
                        alt={t("aspectRatioPage.childrenLabel")}
                        ratio="1:1"
                    >
                        <Text
                            style={CHILDREN_TEXT_STYLE}
                            variant="titleMediumSize"
                            color="high"
                        >
                            {t("aspectRatioPage.childrenLabel")}
                        </Text>
                    </AspectRatio>)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("aspectRatioPage.statesText")}
            </Paragraph>
            <Preview
                caption={t("aspectRatioPage.statesCaption")}
            >
                {
                    renderCell(t("aspectRatioPage.stateEmpty"), <AspectRatio
                        alt={t("aspectRatioPage.imageAlt")}
                        ratio="4:3"
                    />, MEDIUM_CELL_STYLE)
                }
                {
                    renderCell(t("aspectRatioPage.stateFailed"), <AspectRatio
                        alt={t("aspectRatioPage.imageAlt")}
                        onRetry={() => undefined}
                        source={BROKEN_SOURCE}
                        ratio="4:3"
                    />, MEDIUM_CELL_STYLE)
                }
                {
                    renderCell("isRestricted={true}", <AspectRatio
                        alt={t("aspectRatioPage.imageAlt")}
                        isRestricted={true}
                        ratio="4:3"
                    />, MEDIUM_CELL_STYLE)
                }
                {
                    renderCell("isLoading={true}", <Fragment>
                        <AspectRatio
                            alt={t("aspectRatioPage.imageAlt")}
                            source={SAMPLE_PHOTO_SOURCE}
                            isLoading={isReloading}
                            ratio="4:3"
                        />
                        <Button
                            title={t("aspectRatioPage.reload")}
                            isDisabled={isReloading}
                            onPress={handleReload}
                            variant="outline"
                            type="neutral"
                            size="small"
                        />
                    </Fragment>, MEDIUM_CELL_STYLE)
                }
            </Preview>
            <CodeBlock
                code={STATES_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("aspectRatioPage.cornersTitle")}
            id="corners"
        >
            <Paragraph>
                {t("aspectRatioPage.cornersText")}
            </Paragraph>
            <Preview>
                {
                    RADIUS_ROLES.map((radiusRole) => renderCell(`radiusRole="${radiusRole}"`, <AspectRatio
                        alt={t("aspectRatioPage.imageAlt")}
                        source={SAMPLE_PHOTO_SOURCE}
                        radiusRole={radiusRole}
                        flushEdge="top"
                        ratio="4:3"
                    />))
                }
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("aspectRatioPage.a11yAlt"),
                    t("aspectRatioPage.a11yLoading"),
                    t("aspectRatioPage.a11yStates"),
                    t("aspectRatioPage.a11yRetry"),
                    t("aspectRatioPage.a11yFocus"),
                    t("aspectRatioPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("aspectRatioPage.propAlt"),
                        type: "string",
                        name: "alt"
                    },
                    {
                        description: t("aspectRatioPage.propSource"),
                        type: "ImageSourcePropType",
                        name: "source"
                    },
                    {
                        type: "\"1:1\" | \"4:3\" | \"16:9\" | \"21:9\" | \"3:4\" | number",
                        description: t("aspectRatioPage.propRatio"),
                        defaultValue: "\"16:9\"",
                        name: "ratio"
                    },
                    {
                        description: t("aspectRatioPage.propObjectFit"),
                        type: "\"cover\" | \"contain\"",
                        defaultValue: "\"cover\"",
                        name: "objectFit"
                    },
                    {
                        type: "\"auto\" | \"surface\" | \"form\" | \"flush\"",
                        description: t("aspectRatioPage.propRadiusRole"),
                        defaultValue: "\"auto\"",
                        name: "radiusRole"
                    },
                    {
                        description: t("aspectRatioPage.propFlushEdge"),
                        type: "\"top\" | \"bottom\" | \"both\"",
                        defaultValue: "\"top\"",
                        name: "flushEdge"
                    },
                    {
                        description: t("aspectRatioPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("aspectRatioPage.propIsRestricted"),
                        defaultValue: "false",
                        name: "isRestricted",
                        type: "boolean"
                    },
                    {
                        description: t("aspectRatioPage.propOnLoad"),
                        type: "() => void",
                        name: "onLoad"
                    },
                    {
                        description: t("aspectRatioPage.propOnError"),
                        type: "() => void",
                        name: "onError"
                    },
                    {
                        description: t("aspectRatioPage.propOnRetry"),
                        type: "() => void",
                        name: "onRetry"
                    },
                    {
                        description: t("aspectRatioPage.propRenderFallback"),
                        type: "() => ReactNode",
                        name: "renderFallback"
                    },
                    {
                        description: t("aspectRatioPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("aspectRatioPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("aspectRatioPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("aspectRatioPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default AspectRatioPage;
